import { useEffect, useState } from "react";
import { getDataGridColumns } from "./dataGridColumns";
import { getDataGridRows } from "./dataGridRows";
import CurrentDataGrid from "./CurrentDataGrid";
import { useAppDispatch, useAppSelector } from "../../../store/types";
import {
  MISSED_CALL_STATUS,
  REJECTED_CALL_STATUS,
  VAR_TRANSCRIPTS_ENABLED,
  TICKET_OR_CASE_NUMBER_NEW,
  CALL_METHOD_ENDED,
} from "../../../constants";
import { getCcpContact } from "../../../services/ccp";
import { addCallHistory } from "../../../services/history";
import {
  saveContactToHistoryAction,
  updateCurrentCtr,
} from "../../../services/current";
import { getDataGridAttributes } from "./dataGridAttributes";
import useTextTranslate from "../hooks/useTextTranslate";
import { screenRecordingActions } from "../../../slices/screenRecordingSlice";
import {
  getTicketOrCaseNumberSelector,
  getTicketOrCaseNumberDefaultSelector,
  getSaveContactHistorySelector,
  getSaveContactHistoryDataSelector,
} from "../../../selectors/currentSelector";
import {
  getTranslatedTextSelector,
  getTimezoneSelector,
} from "../../../selectors/settingsSelector";
import {
  getCcpContactStatusSelector,
  getCcpContactMethodSelector,
  getCcpContactIdSelector,
  getCcpContactAttributesSelector,
} from "../../../selectors/ccpSelector";
import { getTicketOrCaseFeatureFlagEnabledSelector } from "../../../selectors/featureFlagSelector";
import { getConnectedInstanceSelector } from "../../../selectors/userInstanceSelector";

const Current = () => {
  const ccpContactStatus = useAppSelector(getCcpContactStatusSelector);
  const ccpContactMethod = useAppSelector(getCcpContactMethodSelector);
  const contactId = useAppSelector(getCcpContactIdSelector);
  const contactAttributes = useAppSelector(getCcpContactAttributesSelector);

  const connectedInstance = useAppSelector(getConnectedInstanceSelector);
  const ticketOrCaseFeatureFlagEnabled = useAppSelector(
    getTicketOrCaseFeatureFlagEnabledSelector,
  );
  const translatedText = useAppSelector(getTranslatedTextSelector);
  const userTimezone = useAppSelector(getTimezoneSelector);

  const ticketOrCaseNumber = useAppSelector(getTicketOrCaseNumberSelector);
  const ticketOrCaseNumberDefault = useAppSelector(
    getTicketOrCaseNumberDefaultSelector,
  );
  const history = useAppSelector(getSaveContactHistorySelector);
  const historyData = useAppSelector(getSaveContactHistoryDataSelector);

  const dispatch = useAppDispatch();

  const [dataGridRows, setDataGridRows] = useState<
    Array<{ id: number; name: string; value: string }>
  >([]);
  const instanceId = connectedInstance ? connectedInstance.instanceId : null;
  const timeStampTranslatedText = useTextTranslate("Time");

  useEffect(() => {
    if (!!history) {
      handleCurrentContactClear(historyData);
    }
  }, [history]);

  useEffect(() => {
    if (contactAttributes && Object.keys(contactAttributes).length > 0) {
      setDataGridRows(
        getDataGridRows(
          contactAttributes,
          timeStampTranslatedText,
          connectedInstance?.appConfig?.contactAttributeOrdering,
          userTimezone,
        ),
      );
    }
  }, [contactAttributes, translatedText, userTimezone]);

  const handleCurrentContactClear = async (
    historyData: Record<string, unknown> | null,
  ) => {
    if (historyData && Object.keys(historyData).length > 0) {
      const ticketOrCaseNumberValue =
        ticketOrCaseNumber ??
        ticketOrCaseNumberDefault ??
        TICKET_OR_CASE_NUMBER_NEW;
      const currentSaveAttributes = await getDataGridAttributes(
        dataGridRows,
        historyData,
        ticketOrCaseNumberValue,
      );
      currentSaveAttributes.instanceId = connectedInstance?.instanceId ?? null;
      // TODO - to be re-implemented due to history not saving when default ticket# is used
      //      if (ticketOrCaseNumberValue !== ticketOrCaseNumberDefault) {
      if (
        !!ticketOrCaseNumberValue &&
        Object.keys(currentSaveAttributes ?? {}).length !== 0
      ) {
        let areTranscriptsEnabledForCall = false;
        const areTranscriptsEnabledInCallFlow =
          (contactAttributes?.[VAR_TRANSCRIPTS_ENABLED]?.value ?? "true") ===
          "true";
        const callMethod = String(currentSaveAttributes?.["Method"] ?? "");

        if (
          callMethod &&
          [
            MISSED_CALL_STATUS,
            REJECTED_CALL_STATUS,
            CALL_METHOD_ENDED,
          ].includes(callMethod)
        ) {
          areTranscriptsEnabledForCall = false;
        } else {
          areTranscriptsEnabledForCall = areTranscriptsEnabledInCallFlow;
        }

        await Promise.all([
          dispatch(
            addCallHistory({
              ...currentSaveAttributes,
              areTranscriptsEnabledForCall,
            }),
          ),
          dispatch(
            updateCurrentCtr({
              instanceId,
              contactId,
              ticketOrCaseNumberValue,
            }),
          ),
          dispatch(screenRecordingActions.resetContactRecordingStateAction()),
        ]);
      }

      await dispatch(
        getCcpContact({
          contactId: null,
          contactAttributes: null,
          contactData: null,
        }),
      );
      await dispatch(
        saveContactToHistoryAction({ history: false, historyData: null }),
      );
      setDataGridRows([]);
    }
  };

  return (
    <CurrentDataGrid
      rows={dataGridRows}
      columns={
        getDataGridColumns(
          ccpContactStatus,
          ccpContactMethod,
          ticketOrCaseFeatureFlagEnabled ?? false,
        ) as Record<string, unknown>[]
      }
    />
  );
};

export default Current;
